<template>
  <div>
    <template v-if="(state && state.matches('reset')) || false">
      <translate tag="h3"> Password reset request </translate>

      <translate tag="p">
        If you have an account*, we've emailed you instructions for setting your
        password. You should be receiving them shortly. (link expires in 3 days)
      </translate>
      <p>
        <i18n-t
          keypath="If you don't receive an email, please make sure you've entered the address you registered with, and check your spam folder. {contactUs} with any problems."
        >
          <template #contactUs>
            <translate
              tag="a"
              :href="`https://www.pitchup.com/${
                $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
              }about/contact/`"
              target="_blank"
            >
              Contact us
            </translate>
          </template>
        </i18n-t>
      </p>
      <div class="help-text">
        <translate>
          *Please note: you'll only receive a reset password email if you have a
          Pitchup.com account.
        </translate>
        <br />
        <translate>
          You will have an account if you added a password when booking, signed
          up on our website, logged in with Facebook/Google, or your email
          address is linked to a Pitchup.com booking.
        </translate>
      </div>
    </template>
    <template v-else>
      <translate tag="h3"> Forgotten your password? </translate>
      <translate tag="p">
        Enter your email address below, and we will email instructions for
        setting a new one if you have an account.*
      </translate>
      <translate tag="div" class="help-text">
        *You will have an account if you set a password when booking, or signed
        up for an account on our website
      </translate>
      <br />
      <Form @submit="submit">
        <base-input
          type="email"
          name="email"
          :validation-rules="emailValidationRules"
          :required="true"
          :model-value="email"
          @input="updateEmail($event)"
        >
          <template #label>
            <translate>Email address</translate>
          </template>
          <template #errorMessage="{ failedRules }">
            <translate v-if="failedRules === 'required'" tag="span">
              Please enter your email address
            </translate>
            <translate v-if="failedRules === 'email'">
              Please enter a valid email address
            </translate>
          </template>
        </base-input>
        <base-submit-button
          class="submit-button primary asblock"
          :submitting="state ? state.matches('resetting') : false"
        >
          <translate>Reset my password</translate>
          <template #submittingText>
            <translate>Resetting...</translate>
          </template>
        </base-submit-button>
      </Form>
    </template>
    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import { Form } from 'vee-validate'
import * as z from 'zod'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { getResetPasswordServiceFactory } from '../inversify.config'
import {
  ResetPasswordContext,
  ResetPasswordService,
  ResetPasswordStateType,
} from '../machines/resetPasswordMachine'

type Errors = Record<string, string[]>

@Component({
  components: { Form },
  emits: ['updateEmail'],
})
class ResetPassword extends Vue {
  declare $refs: {
    resetPasswordForm: InstanceType<typeof Form>
  }

  emailValidationRules = z
    .string({ required_error: 'required' })
    .email({ message: 'email' })
    .min(1, { message: 'required' })

  serviceFactory = getResetPasswordServiceFactory()

  service: ResetPasswordService | null = null
  context: ResetPasswordContext | null = null
  state: ResetPasswordStateType | null = null

  @Prop()
  userEmail: string

  get email() {
    return this.context?.email
  }

  created() {
    this.registerToResetPasswordMachine()
  }

  mounted() {
    if (this.userEmail) {
      this.updateEmail(this.userEmail)
    }
  }

  get head() {
    return {
      bodyAttrs: {
        class: 'reset-password-open',
      },
    }
  }

  @Watch('errors')
  onErrorsChanged(errors: Errors) {
    this.$refs.resetPasswordForm.resetForm()
    this.$refs.resetPasswordForm.setErrors(errors)
  }

  submit() {
    this.service?.send('SUBMIT')
    this.$emit('updateEmail', this.context?.email)
  }

  updateEmail(value: string) {
    this.service?.send('CHANGE_EMAIL', { value })
  }

  private registerToResetPasswordMachine() {
    const service = this.serviceFactory({
      email: this.userEmail,
      next: this.$route.fullPath,
    }) as ResetPasswordService

    this.service = service
      .onTransition((state) => {
        if (state.changed) {
          this.state = state
          this.context = state.context
        }
      })
      .start()
  }
}

export default toNative(ResetPassword)
</script>

<style>
.reset-password-open .account-open #page-wrapper::after {
  background-color: rgba(0, 0, 0, 0.6);
  bottom: 0;
  content: '';
  display: block;
  left: 0;
  position: fixed;
  right: 0;
  top: 14vw;
  z-index: 9;
}

.reset-password-open #user {
  overflow: hidden;
}
</style>
<style scoped>
p {
  font-size: var(--body);
  margin: 0 0 1em 0;
}

h3 {
  margin-right: 2em;
}

.submit-button {
  margin: 1em 0;
}
</style>
